import { createContext, useContext } from "react";
import type { Brand } from "./api/index.js";

/**
 * What every signed-in screen is given: its workspace, the brands in it, and
 * the report it is about.
 *
 * Resolved once, by the signed-in layout in `app.tsx`, and read here by the
 * route that draws each screen. The screens themselves still take these as
 * props — a screen never picks its own tenant, and a test can hand it any.
 *
 * A context rather than a store: it is server state already fetched and a
 * report id already in the address, passed down one level. Nothing writes it.
 */
export interface SignedIn {
  readonly workspaceId: string;
  readonly brands: readonly Brand[];
  /** Null before the workspace has a report that is measuring. */
  readonly reportId: string | null;
}

export const SignedInContext = createContext<SignedIn | null>(null);

/** The signed-in screen's context. Outside the layout it throws, not guesses. */
export function useSignedIn(): SignedIn {
  const value = useContext(SignedInContext);
  if (value === null) {
    throw new Error("useSignedIn is only for screens inside the signed-in layout");
  }
  return value;
}
